import { useMemo } from 'react';
import { MarketDisplayData } from 'src/components/MarketSelector/MarketSelectorTable/types';
import { ZERO } from 'src/constants/math';
import { marketHasInsuranceFund } from 'src/hooks/useInfoForCurrentlySelectedMarket';
import useVelocityStore from 'src/stores/VelocityStore/useVelocityStore';
import { PerpMarketInfo } from 'src/stores/types';
import {
	ENUM_UTILS,
	MarketDetails24H,
	MarketId,
	UIMarket,
} from '@velocity-exchange/common';
import {
	PerpMarketConfig,
	VelocityClient,
	isVariant,
	BigNum,
	BASE_PRECISION_EXP,
	MarketType,
	MarketStatus,
} from '@velocity-exchange/sdk';
import { usePerpMarketsLookup } from 'src/hooks/perpMarkets/usePerpMarketsLookup';
import useFavouriteMarkets from 'src/hooks/useFavouriteMarkets';
import useHiddenPerpMarkets from 'src/hooks/useHiddenPerpMarkets';
import useMarketsInfoStore from 'src/stores/useMarketsInfoStore';
import useMarketsSortedByCriteria from 'src/hooks/useMarketsSortedByCriteria';
import { getMarketSelectorPricePercentChange } from 'src/components/MarketSelector/utils';
import { useGetMarkPriceForMarket } from 'src/hooks/useMarkPrice';
import {
	PredictedFunding,
	useLive24hMarketData,
	useLivePredictedFundings,
	useLiveMarketsSnapshot,
} from 'src/stores/marketLiveState';

export default function useMarketSelectorPerpMarkets() {
	const perpMarketsLookup = usePerpMarketsLookup();
	const hiddenPerpMarkets = useHiddenPerpMarkets();

	const velocityClient = useVelocityStore((s) => s.velocityClient.client);
	const marketsData = useLive24hMarketData();
	const predictedFundings = useLivePredictedFundings();
	const getMarketInfoByIndexAndType = useMarketsInfoStore(
		(s) => s.getMarketInfoByIndexAndType
	);
	const { favouriteMarketsIncludes } = useFavouriteMarkets();
	const getMarkPriceForMarket = useGetMarkPriceForMarket();

	const defaultSortedPerpMarkets = useMarketsSortedByCriteria({
		marketType: MarketType.PERP,
		sortBy: ['isFavorited', '24hVolume'],
	});

	// `getMarkPriceForMarket` reads the live-state module imperatively and won't
	// re-render on its own. Subscribe to the bulk live-price snapshot for the
	// sorted markets so the rendering memo (which also drives price-based
	// sorting) recomputes at the module's change-gated 250ms flush cadence
	// instead of a blanket ~1s tick.
	const priceSnapshotMarketIds = useMemo(
		() =>
			defaultSortedPerpMarkets.map((mkt) =>
				MarketId.createPerpMarket(mkt.marketIndex)
			),
		[defaultSortedPerpMarkets]
	);
	const priceSnapshot = useLiveMarketsSnapshot(priceSnapshotMarketIds);

	return useMemo(() => {
		if (!velocityClient) {
			return [];
		}

		const mappedMarkets = defaultSortedPerpMarkets
			// Don't show hidden low-activity perp markets.
			.filter((mktSortingInfo) => {
				return !hiddenPerpMarkets.has(mktSortingInfo.marketIndex);
			})
			.map((mktSortingInfo) => {
				return {
					marketInfo: getMarketInfoByIndexAndType(
						mktSortingInfo.marketIndex,
						mktSortingInfo.marketType
					) as PerpMarketInfo,
					marketConfig: perpMarketsLookup[mktSortingInfo.marketIndex],
					isHot: mktSortingInfo.isHot,
				};
			});

		return getPerpMarketsRenderingInfo(
			mappedMarkets,
			velocityClient,
			marketsData,
			predictedFundings,
			favouriteMarketsIncludes,
			getMarkPriceForMarket
		).filter((mkt) => {
			return (
				!ENUM_UTILS.match(
					mkt.marketAccount?.status,
					MarketStatus.REDUCE_ONLY
				) &&
				!ENUM_UTILS.match(mkt.marketAccount?.status, MarketStatus.SETTLEMENT)
			);
		});
	}, [
		velocityClient,
		defaultSortedPerpMarkets,
		marketsData,
		predictedFundings,
		favouriteMarketsIncludes,
		getMarketInfoByIndexAndType,
		perpMarketsLookup,
		getMarkPriceForMarket,
		priceSnapshot,
		hiddenPerpMarkets,
	]);
}

const getPerpMarketsRenderingInfo = (
	defaultSortedPerpMarkets: {
		marketInfo: PerpMarketInfo;
		marketConfig: PerpMarketConfig;
		isHot: boolean;
	}[],
	velocityClient: VelocityClient,
	marketsData: MarketDetails24H[],
	predictedFundings: PredictedFunding[],
	favouriteMarketsIncludes: (marketId: MarketId) => boolean,
	getMarkPriceForMarket: (marketId: MarketId) => BigNum
): MarketDisplayData[] => {
	return defaultSortedPerpMarkets.map<MarketDisplayData>((mktInfo) => {
		const marketId = MarketId.createPerpMarket(
			mktInfo.marketConfig.marketIndex
		);

		if (!mktInfo.marketInfo) {
			const marketConfig = mktInfo.marketConfig;

			// Market Info Hasn't Loaded Yet .. Return default/blank values
			return {
				symbol: marketConfig.symbol,
				baseAssetSymbol: marketConfig.baseAssetSymbol,
				market: UIMarket.createPerpMarket(marketConfig.marketIndex),
				price: null,
				pricePercentChange: null,
				price24hAgo: null,
				volume: null,
				high: null,
				low: null,
				openInterest: undefined,
				funding: undefined,
				funding24H: undefined,
				launchTs: 0,
				config: marketConfig,
				noInsuranceFund: false,
				marketAccount: undefined,
				isHot: false,
				isFavorite: favouriteMarketsIncludes(marketId),
			};
		}

		const mkt = mktInfo.marketInfo.config;

		const perpMarketAccount = velocityClient.getPerpMarketAccount(
			mkt.marketIndex
		);
		const uiMarket = UIMarket.createPerpMarket(mkt.marketIndex);
		const market24h = marketsData.find(
			(mktData) =>
				mktData.marketIndex === uiMarket.marketIndex &&
				isVariant(mktData.marketType, 'perp')
		);
		const markPrice = getMarkPriceForMarket(marketId);
		const baseAmount = (perpMarketAccount?.baseAssetAmountLong ?? ZERO).add(
			perpMarketAccount?.baseAssetAmountShort.abs() ?? ZERO
		);

		const openInterest =
			baseAmount && markPrice
				? BigNum.from(baseAmount, BASE_PRECISION_EXP).mul(markPrice).toNum()
				: undefined;

		const noInsuranceFund = !marketHasInsuranceFund(perpMarketAccount);

		const avgLongFunding = market24h?.avgLongFunding ?? 0;
		const avgShortFunding = market24h?.avgShortFunding ?? 0;

		const funding24H =
			Math.abs(avgLongFunding) >= Math.abs(avgShortFunding)
				? avgLongFunding
				: avgShortFunding;

		return {
			symbol: mkt.symbol,
			baseAssetSymbol: mkt.baseAssetSymbol,
			market: uiMarket,
			price: markPrice,
			pricePercentChange: getMarketSelectorPricePercentChange(
				markPrice.toNum(),
				market24h?.price24hAgo ?? 0
			),
			price24hAgo: market24h?.price24hAgo ?? 0,
			volume: market24h?.quoteVolume ?? 0,
			high: market24h?.priceHigh ?? null,
			low: market24h?.priceLow ?? null,
			openInterest,
			funding: parseFloat(
				predictedFundings
					.find((funding) => funding.marketIndex === mkt.marketIndex)
					?.fundingRate.toFixed(5) ?? '0'
			),
			funding24H,
			launchTs: mkt.launchTs,
			config: mkt,
			noInsuranceFund,
			marketAccount: mktInfo.marketInfo.account,
			isHot: mktInfo.isHot,
			isFavorite: favouriteMarketsIncludes(marketId),
		};
	});
};
